/**
 * Mesh topology utilities shared by the boolean engine and revolve kernel.
 * Everything operates on plain typed arrays — worker/test friendly.
 */

import type { MeshData } from './types';

/**
 * Compose an additional scale `s` about `anchor` onto an existing per-axis
 * scale map pₖ → centerₖ + (pₖ−centerₖ)·factorsₖ, for one axis. Returns the
 * equivalent (center, factors) pair — this is what lets SketchUp-style
 * opposite-anchored handle drags stay representable as a single Scale node.
 */
export function composeAxisScale(
  center: readonly [number, number, number],
  factors: readonly [number, number, number],
  axis: 0 | 1 | 2,
  s: number,
  anchor: number,
): { center: [number, number, number]; factors: [number, number, number] } {
  const f = factors[axis];
  const c = center[axis];
  const alpha = f * s;
  // Combined affine map pₖ → α·pₖ + β; its fixed point is the new center.
  const beta = anchor * (1 - s) + s * c * (1 - f);
  const newCenter: [number, number, number] = [center[0], center[1], center[2]];
  const newFactors: [number, number, number] = [factors[0], factors[1], factors[2]];
  if (Math.abs(1 - alpha) > 1e-9) newCenter[axis] = beta / (1 - alpha);
  newFactors[axis] = alpha;
  return { center: newCenter, factors: newFactors };
}

/** Signed volume of an indexed triangle mesh (positive = outward winding). */
export function signedVolume(positions: Float32Array, indices: Uint32Array): number {
  let v = 0;
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t]! * 3;
    const b = indices[t + 1]! * 3;
    const c = indices[t + 2]! * 3;
    const ax = positions[a]!, ay = positions[a + 1]!, az = positions[a + 2]!;
    const bx = positions[b]!, by = positions[b + 1]!, bz = positions[b + 2]!;
    const cx = positions[c]!, cy = positions[c + 1]!, cz = positions[c + 2]!;
    v += ax * (by * cz - bz * cy) + ay * (bz * cx - bx * cz) + az * (bx * cy - by * cx);
  }
  return v / 6;
}

/** Flip winding in place if the mesh is inside-out. */
export function ensureOutward(mesh: MeshData): MeshData {
  if (signedVolume(mesh.positions, mesh.indices) >= 0) return mesh;
  const idx = mesh.indices;
  for (let t = 0; t < idx.length; t += 3) {
    const tmp = idx[t + 1]!;
    idx[t + 1] = idx[t + 2]!;
    idx[t + 2] = tmp;
  }
  const n = mesh.normals;
  for (let i = 0; i < n.length; i++) n[i] = -n[i]!;
  return mesh;
}

/**
 * Extract display edges from a MERGED indexed mesh: every edge whose two
 * faces meet at more than `angleDeg`, plus boundary edges. Vertices are keyed
 * by quantized position so exact-duplicate verts share topology.
 */
export function sharpEdges(
  positions: Float32Array,
  indices: Uint32Array,
  angleDeg = 30,
): Float32Array {
  const cosLimit = Math.cos((angleDeg * Math.PI) / 180);
  const posKey = (i: number): string =>
    `${Math.round(positions[i * 3]! * 1e4)}:${Math.round(positions[i * 3 + 1]! * 1e4)}:${Math.round(positions[i * 3 + 2]! * 1e4)}`;

  // Canonical vertex id per position.
  const canon = new Map<string, number>();
  const vertId = new Uint32Array(positions.length / 3);
  for (let i = 0; i < vertId.length; i++) {
    const k = posKey(i);
    const existing = canon.get(k);
    if (existing === undefined) {
      canon.set(k, i);
      vertId[i] = i;
    } else vertId[i] = existing;
  }

  const faceNormal = (t: number): [number, number, number] => {
    const a = indices[t]! * 3, b = indices[t + 1]! * 3, c = indices[t + 2]! * 3;
    const ux = positions[b]! - positions[a]!, uy = positions[b + 1]! - positions[a + 1]!, uz = positions[b + 2]! - positions[a + 2]!;
    const vx = positions[c]! - positions[a]!, vy = positions[c + 1]! - positions[a + 1]!, vz = positions[c + 2]! - positions[a + 2]!;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    return [nx / len, ny / len, nz / len];
  };

  const edgeFaces = new Map<string, { tris: number[]; a: number; b: number }>();
  for (let t = 0; t < indices.length; t += 3) {
    for (let e = 0; e < 3; e++) {
      const a = vertId[indices[t + e]!]!;
      const b = vertId[indices[t + ((e + 1) % 3)]!]!;
      if (a === b) continue; // degenerate
      const key = a < b ? `${a}_${b}` : `${b}_${a}`;
      let rec = edgeFaces.get(key);
      if (!rec) {
        rec = { tris: [], a, b };
        edgeFaces.set(key, rec);
      }
      rec.tris.push(t);
    }
  }

  const out: number[] = [];
  for (const { tris, a, b } of edgeFaces.values()) {
    let sharp = tris.length !== 2;
    if (!sharp) {
      const n0 = faceNormal(tris[0]!);
      const n1 = faceNormal(tris[1]!);
      sharp = n0[0] * n1[0] + n0[1] * n1[1] + n0[2] * n1[2] < cosLimit;
    }
    if (sharp) {
      out.push(
        positions[a * 3]!, positions[a * 3 + 1]!, positions[a * 3 + 2]!,
        positions[b * 3]!, positions[b * 3 + 1]!, positions[b * 3 + 2]!,
      );
    }
  }
  return new Float32Array(out);
}
